import React from "react";
import { useRouteError, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Home, RefreshCw, Shield, AlertCircle, Bug } from "lucide-react";
import { useAppSelector } from "../redux/hooks";

export function ErrorBoundary() {
  const error = useRouteError() as any;
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const handleGoHome = () => {
    if (user) {
      // Navigate based on user role
      if (user?.role === "super_admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/pharmacy/dashboard");
      }
    } else {
      navigate("/");
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const getErrorMessage = () => {
    if (error?.status === 404) {
      return "Page not found";
    }
    if (error?.status >= 500) {
      return "Server error occurred";
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.statusText) {
      return error.statusText;
    }
    return "An unexpected error occurred";
  };

  const getErrorCode = () => {
    if (error?.status) {
      return error.status.toString();
    }
    return "ERROR";
  };

  console.error("Application Error:", error);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-gray-50 to-red-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            Courier-RX
          </h1>
          <p className="text-gray-600">
            Enterprise Pharmacy Delivery Platform
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <div className="p-8 text-center">
            {/* Error Icon */}
            <div className="flex items-center justify-center mb-6">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-12 h-12 text-red-500" />
              </div>
            </div>

            {/* Error Content */}
            <div className="mb-8">
              <h1 className="text-6xl font-bold text-red-300 mb-2">
                {getErrorCode()}
              </h1>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Something went wrong
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto mb-4">
                {getErrorMessage()}
              </p>
              
              {/* Technical Details (Development Mode) */}
              {import.meta.env.DEV && error?.stack && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-2">
                    <Bug className="w-4 h-4" />
                    Technical Details (Development)
                  </summary>
                  <div className="bg-gray-50 rounded-lg p-4 text-xs font-mono text-gray-700 max-h-32 overflow-auto border">
                    <pre className="whitespace-pre-wrap break-words">
                      {error.stack}
                    </pre>
                  </div>
                </details>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-1 justify-center items-center">
              <Button
                onClick={handleRefresh}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 text-sm font-medium"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              <Button
                onClick={handleGoHome}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-2.5 text-sm font-medium"
                size="lg"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                If this error persists, please contact your system administrator or 
                <span className="text-red-600 font-medium"> support team</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Additional Information */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            {/* Error ID: {Date.now().toString(36)} • {new Date().toISOString()} */}
          </p>
        </div>
      </div>
    </div>
  );
}

// Classic React Error Boundary for catching JavaScript errors
export class ReactErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("React Error Boundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// Fallback component for React Error Boundary
function ErrorFallback({ error }: { error?: Error }) {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);

  const handleGoHome = () => {
    if (user) {
      if (user.role === "super_admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/pharmacy/dashboard");
      }
    } else {
      navigate("/auth/login");
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-gray-50 to-red-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            Courier-RX
          </h1>
          <p className="text-gray-600">
            Enterprise Pharmacy Delivery Platform
          </p>
        </div>

        <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
          <div className="p-8 text-center">
            {/* Error Icon */}
            <div className="flex items-center justify-center mb-6">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
                <Bug className="w-12 h-12 text-red-500" />
              </div>
            </div>

            {/* Error Content */}
            <div className="mb-8">
              <h1 className="text-6xl font-bold text-red-300 mb-2">ERROR</h1>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Application Error
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto mb-4">
                {error?.message || "An unexpected error occurred in the application"}
              </p>

              {/* Technical Details (Development Mode) */}
              {import.meta.env.DEV && error?.stack && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-2 mb-3">
                    <Bug className="w-4 h-4" />
                    Technical Details (Development)
                  </summary>
                  <div className="bg-gray-50 rounded-lg p-4 text-xs font-mono text-gray-700 max-h-32 overflow-auto border">
                    <pre className="whitespace-pre-wrap break-words">
                      {error.stack}
                    </pre>
                  </div>
                </details>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                onClick={handleRefresh}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 text-sm font-medium"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              <Button
                onClick={handleGoHome}
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-2.5 text-sm font-medium"
                size="lg"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>

            {/* Help Text */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                If this error persists, please contact your system administrator or 
                <span className="text-red-600 font-medium"> support team</span>
              </p>
            </div>
          </div>
        </Card>

        {/* Additional Information */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Error ID: {Date.now().toString(36)} • {new Date().toISOString()}
          </p>
        </div>
      </div>
    </div>
  );
}

